import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { lessonService } from '@/services/lessonService'
import type { CreateLessonDTO, UpdateLessonDTO } from '@/types/lesson'

export function useLesson(id: string) {
  return useQuery({
    queryKey: ['lessons', id],
    queryFn: async () => {
      const response = await lessonService.getById(id)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch lesson')
      }
      return response.data
    },
    enabled: !!id,
  })
}

export function useLessonsByModule(moduleId: string) {
  return useQuery({
    queryKey: ['lessons', 'module', moduleId],
    queryFn: async () => {
      const response = await lessonService.getByModuleId(moduleId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch lessons')
      }
      return response.data
    },
    enabled: !!moduleId,
  })
}

export function useAllLessons() {
  return useQuery({
    queryKey: ['lessons'],
    queryFn: async () => {
      const response = await lessonService.getAll()
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch lessons')
      }
      return response.data
    },
  })
}

export function useCreateLesson() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateLessonDTO) => lessonService.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] })
      if (response.data) {
        queryClient.invalidateQueries({ queryKey: ['lessons', 'module', response.data.moduleId] })
      }
    },
  })
}

export function useUpdateLesson() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLessonDTO }) =>
      lessonService.update(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] })
      if (response.data) {
        queryClient.invalidateQueries({ queryKey: ['lessons', response.data.id] })
        queryClient.invalidateQueries({ queryKey: ['lessons', 'module', response.data.moduleId] })
      }
    },
  })
}

export function useDeleteLesson() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => lessonService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] })
    },
  })
}

export function useSetLessonPreviewable() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, previewable }: { id: string; previewable: boolean }) =>
      lessonService.setPreviewable(id, previewable),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] })
      queryClient.invalidateQueries({ queryKey: ['lessons', variables.id] })
    },
  })
}

export function useReorderLessons() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (lessonIds: string[]) => lessonService.reorder(lessonIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] })
    },
  })
}

export function useLessonContentUrl(lessonId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['lessons', lessonId, 'content-url'],
    queryFn: async () => {
      const response = await lessonService.getContentUrl(lessonId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch content URL')
      }
      return response.data
    },
    enabled: !!lessonId && enabled,
    staleTime: 12 * 60 * 1000, // 12 minutes (buffer before 15 min URL expiry)
    gcTime: 12 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
